import * as React from "react"

import * as PopoverPrimitive from "@radix-ui/react-popover"
import { cva } from "class-variance-authority"
import { Check, Plus, Tag as TagIcon } from "lucide-react"

import { cn } from "../../cn"
import { buttonVariants } from "../Button/Button"
import { ColorSwatch } from "../ColorSwatch/ColorSwatch"
import { SearchInput } from "../SearchInput/SearchInput"
import {
  resultCountMessage,
  SearchResultsStatus,
} from "../Select/SearchResultsStatus"
import { Tooltip } from "../Tooltip/Tooltip"

/**
 * The built-in triggers mark a disabled state with `aria-disabled` rather than
 * `disabled`, so they stay hoverable and focusable and the tooltip naming the
 * assigned tags still opens. These mirror `buttonVariants`' `disabled:` styles.
 */
const DISABLED_TRIGGER_CLASSES = {
  secondary:
    "aria-disabled:cursor-default aria-disabled:opacity-50 aria-disabled:hover:bg-surface-default",
  ghost:
    "aria-disabled:cursor-default aria-disabled:opacity-50 aria-disabled:hover:bg-transparent aria-disabled:hover:text-coval-text-muted",
} as const

/**
 * A disabled built-in trigger stays a live element so its tooltip opens, so it
 * swallows the clicks a native disabled button would never emit, keeping them
 * from reaching a clickable row or container around the select.
 */
const swallowActivation = (event: React.SyntheticEvent) => {
  event.stopPropagation()
}

const SWALLOWED_ACTIVATION = {
  onClick: swallowActivation,
  onMouseDown: swallowActivation,
  onMouseUp: swallowActivation,
  onPointerDown: swallowActivation,
  onPointerUp: swallowActivation,
  onKeyDown: swallowActivation,
} as const

/** Whether `text` opens with `word` as a whole word, ignoring case. */
function startsWithWord(text: string, word: string): boolean {
  const head = text.slice(0, word.length)
  const next = text.charAt(word.length)
  return (
    head.toLowerCase() === word.toLowerCase() &&
    (next === "" || /\W/.test(next))
  )
}

/* ── Default palette ──────────────────────────────────────────
 * Presentational fallback so tags without an explicit color still
 * render a stable, distinct hue. Consumers normally pass real colors.
 */

// The shared Coval Accessible Data Palette (chart-tokens.css), referenced as
// CSS vars so the fallback hues match the app's chart/series identity colors
// and stay theme-aware. Order mirrors seriesPalette's SERIES_RAMPS.
const DEFAULT_TAG_PALETTE = [
  "var(--coval-chart-series-1)",
  "var(--coval-chart-series-2)",
  "var(--coval-chart-series-3)",
  "var(--coval-chart-series-4)",
  "var(--coval-chart-series-5)",
  "var(--coval-chart-series-6)",
  "var(--coval-chart-series-7)",
  "var(--coval-chart-series-8)",
]

function fallbackColor(value: string): string {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0
  }
  const index = Math.abs(hash) % DEFAULT_TAG_PALETTE.length
  return DEFAULT_TAG_PALETTE[index]
}

function resolveColor(option: TagSelectOption): string {
  return option.color || fallbackColor(option.value)
}

/* ── Variants ─────────────────────────────────────────────── */

const tagSelectContentVariants = cva([
  "z-50 w-64 rounded-md p-1",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
])

/* ── Types ────────────────────────────────────────────────── */

export interface TagSelectOption {
  value: string
  label: string
  /** Accent color (any CSS color string). Falls back to a stable hue. */
  color?: string
}

export interface TagOptionCheckboxProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  checked: boolean
}

/** The non-interactive checkbox mark used by tag option rows; the surrounding menu item owns the interaction so it can sit inside buttons, context-menu items, and rows with inline tag editors. */
const TagOptionCheckbox = React.forwardRef<
  HTMLSpanElement,
  TagOptionCheckboxProps
>(({ checked, className, ...props }, ref) => (
  <span
    ref={ref}
    aria-hidden="true"
    data-state={checked ? "checked" : "unchecked"}
    className={cn(
      "flex size-4 shrink-0 items-center justify-center rounded border",
      checked
        ? "border-coval-contrast bg-coval-contrast text-coval-text-on-contrast"
        : "border-coval-border-control bg-surface-default",
      className,
    )}
    {...props}
  >
    {checked ? <Check className="size-3" /> : null}
  </span>
))

TagOptionCheckbox.displayName = "TagOptionCheckbox"

export interface TagSelectProps {
  /** Every tag that can be selected. */
  options: TagSelectOption[]
  /** Currently selected tag values. */
  value: string[]
  onValueChange: (next: string[]) => void
  /** Trigger button style. Ignored when `trigger` is supplied. */
  variant?: "secondary" | "ghost"
  /**
   * Renders the trigger as a labelled button — a tag glyph, this text, then
   * the selected tags' dots — instead of the fixed-footprint dot cluster.
   * For headers, where the control sits in a row of labelled buttons rather
   * than in a table cell. Ignored when `trigger` is supplied.
   */
  triggerLabel?: string
  /** How many tags to show before collapsing the remainder to "+N". */
  maxVisible?: number
  disabled?: boolean
  searchPlaceholder?: string
  /** Shown in the menu when no tags exist at all. */
  emptyLabel?: string
  className?: string
  "aria-label"?: string
  "data-testid"?: string
  /**
   * Replaces the built-in dot trigger. Must be a single focusable element —
   * it receives the popover's trigger props via `asChild`.
   */
  trigger?: React.ReactElement
  /** Offer a "Create <query>" row when the search finds no exact match. */
  allowCreate?: boolean
  /** Called with the raw query when the create row is chosen. */
  onCreateOption?: (label: string) => void
  /**
   * Replaces an option's leading dot — the hook for making the dot recolor the
   * tag. Receives the color the plain dot would have carried. A render prop
   * rather than a node on the option so it only runs for visible rows.
   */
  renderOptionDot?: (
    option: TagSelectOption,
    meta: { color: string },
  ) => React.ReactNode
  /**
   * Replaces an option's label — the hook for renaming it in place. The node
   * owns its own flex sizing, and an unselected row hands its selection target
   * to the space beside the label rather than to the label itself.
   */
  renderOptionLabel?: (
    option: TagSelectOption,
    meta: { selected: boolean },
  ) => React.ReactNode
  /** Blocks further additions once reached. Removal stays available. */
  maxSelected?: number
  /** Shows the current tags but disables every mutation. */
  readOnly?: boolean
  /** Replaces the option list with a loading row. */
  isLoading?: boolean
  /**
   * Holds the menu open regardless of what is dismissing it. Needed when a
   * slot renders its own layer — a nested popover portals outside this menu,
   * so a pointer-down inside it reads as an outside interaction here and would
   * otherwise close the menu out from under it.
   */
  keepOpen?: boolean
  /**
   * Controls the menu from outside. Omit to let the control own its own open
   * state — the common case. Supplied when a parent already tracks which of
   * its rows is expanded, as a filter menu does.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Whether closing the menu should return focus to its trigger. */
  restoreFocusOnClose?: boolean
  /** Menu placement against the trigger. */
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  /**
   * Lets the menu flip and shift to stay on screen. Off by default because the
   * dot-cluster trigger anchors its menu to a fixed corner, and a menu that
   * repositions itself there reads as drifting away from the cell it belongs to.
   */
  avoidCollisions?: boolean
  /** Shown when the search matches none of the options. */
  noResultsLabel?: string
}

/* ── Component ────────────────────────────────────────────── */

function TagSelect({
  options,
  value,
  onValueChange,
  variant = "ghost",
  triggerLabel,
  maxVisible = 3,
  disabled,
  searchPlaceholder = "Search tags...",
  emptyLabel = "No tags",
  className,
  "aria-label": ariaLabelProp,
  "data-testid": dataTestId,
  trigger,
  allowCreate = false,
  onCreateOption,
  renderOptionDot,
  renderOptionLabel,
  maxSelected,
  readOnly = false,
  isLoading = false,
  keepOpen = false,
  open: openProp,
  onOpenChange,
  restoreFocusOnClose = true,
  side = "bottom",
  align = "end",
  avoidCollisions = false,
  noResultsLabel = "No matching tags",
}: TagSelectProps) {
  const ariaLabel = ariaLabelProp ?? "Tags"
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const open = openProp ?? uncontrolledOpen
  const [search, setSearch] = React.useState("")
  const searchRef = React.useRef<HTMLInputElement>(null)

  const optionByValue = React.useMemo(() => {
    const map = new Map<string, TagSelectOption>()
    for (const option of options) {
      map.set(option.value, option)
    }
    return map
  }, [options])

  const selectedOptions = React.useMemo(
    () =>
      value
        .map((v) => optionByValue.get(v))
        .filter((o): o is TagSelectOption => o !== undefined),
    [value, optionByValue],
  )

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if ((!next && keepOpen) || (next && disabled)) {
        return
      }
      setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [disabled, keepOpen, onOpenChange],
  )

  // Tied to the closed state rather than to the dismissal, so a controlled
  // parent that closes the menu on its own still opens it fresh next time.
  React.useEffect(() => {
    if (!open) {
      setSearch("")
    }
  }, [open])

  // A control disabled while its menu is open keeps the menu mounted behind the
  // disabled trigger, so the options stay live unless the menu is dismissed.
  // Closing directly rather than through `handleOpenChange` because `keepOpen`
  // must not hold a disabled menu open.
  React.useEffect(() => {
    if (disabled && open) {
      setUncontrolledOpen(false)
      onOpenChange?.(false)
    }
  }, [disabled, onOpenChange, open])

  const atMaxSelected = maxSelected !== undefined && value.length >= maxSelected
  const isEditable = !readOnly && !disabled
  const canAdd = isEditable && !atMaxSelected
  const canRemove = isEditable

  // Slot controls mutate too, and they don't go through `toggle`, so a menu
  // that cannot be edited has to withhold them rather than trust each consumer
  // to check.
  const optionDot = isEditable ? renderOptionDot : undefined
  const optionLabel = isEditable ? renderOptionLabel : undefined

  const renderDot = React.useCallback(
    (option: TagSelectOption) => {
      const color = resolveColor(option)
      return optionDot?.(option, { color }) ?? <ColorSwatch color={color} />
    },
    [optionDot],
  )

  const toggle = React.useCallback(
    (tagValue: string) => {
      const isSelected = value.includes(tagValue)
      if (isSelected ? !canRemove : !canAdd) {
        return
      }
      const next = isSelected
        ? value.filter((v) => v !== tagValue)
        : [...value, tagValue]
      onValueChange(next)
    },
    [value, onValueChange, canAdd, canRemove],
  )

  const rawQuery = search.trim()
  const query = rawQuery.toLowerCase()
  const matches = React.useCallback(
    (option: TagSelectOption) =>
      query === "" || option.label.toLowerCase().includes(query),
    [query],
  )

  const pinned = selectedOptions.filter(matches)
  const rest = options.filter((o) => !value.includes(o.value) && matches(o))

  // Only offer creation when the query names something that doesn't exist yet;
  // an exact hit belongs in the list above, not behind a "Create" row. While
  // options are still loading that check can't be trusted — the tag may exist
  // in the part of the catalog that hasn't arrived — so creation waits.
  const canCreate =
    allowCreate &&
    onCreateOption !== undefined &&
    !isLoading &&
    canAdd &&
    query !== "" &&
    !options.some((o) => o.label.toLowerCase() === query)

  const handleCreate = React.useCallback(() => {
    if (!canCreate) {
      return
    }
    onCreateOption?.(rawQuery)
    setSearch("")
  }, [canCreate, onCreateOption, rawQuery])

  const visibleOptions = [...pinned, ...rest]
  const hasResults = visibleOptions.length > 0 || canCreate

  /* ── Trigger content ── */
  const selectedCount = selectedOptions.length
  const triggerTitle =
    selectedOptions.map((o) => o.label).join(", ") || undefined
  const overflow = selectedCount - maxVisible
  /** Names the assigned tags on hover, from outside the popover trigger so the trigger keeps its child. */
  // The labelled trigger's name starts with the text it shows, overflow count
  // included, so a voice command naming that text reaches it. Without an
  // explicit name the shown text is the whole name.
  const shownTriggerText =
    triggerLabel && overflow > 0 ? `${triggerLabel} +${overflow}` : triggerLabel
  const labelledTriggerName = !shownTriggerText
    ? ariaLabel
    : !ariaLabelProp
      ? shownTriggerText
      : startsWithWord(ariaLabelProp, shownTriggerText)
        ? ariaLabelProp
        : `${shownTriggerText}, ${ariaLabelProp}`
  // The popover's id only resolves while it is open.
  const closedControls = open ? {} : { "aria-controls": undefined }
  const withTriggerTooltip = (popoverTrigger: React.ReactElement) =>
    triggerTitle ? (
      <Tooltip content={triggerTitle}>{popoverTrigger}</Tooltip>
    ) : (
      popoverTrigger
    )

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      {trigger ? (
        <PopoverPrimitive.Trigger
          asChild
          disabled={disabled}
          {...closedControls}
        >
          {trigger}
        </PopoverPrimitive.Trigger>
      ) : triggerLabel ? (
        /*
         * Labelled presentation. Content-sized rather than square-anchored, so
         * it lines up with the other buttons in a header row, and the dots
         * follow the label instead of replacing it — the header is the only
         * place some surfaces show which tags are assigned.
         */
        withTriggerTooltip(
          <PopoverPrimitive.Trigger asChild {...closedControls}>
            <button
              type="button"
              aria-label={labelledTriggerName}
              aria-disabled={disabled || undefined}
              {...(disabled ? SWALLOWED_ACTIVATION : undefined)}
              data-testid={dataTestId}
              className={cn(
                buttonVariants({ variant }),
                DISABLED_TRIGGER_CLASSES[variant],
                className,
              )}
            >
              <TagIcon aria-hidden="true" />
              {triggerLabel}
              {selectedCount > 0 && (
                <span className="flex items-center gap-[5px]">
                  {selectedOptions.slice(0, maxVisible).map((option) => (
                    <ColorSwatch
                      key={option.value}
                      color={resolveColor(option)}
                      className="size-2.5"
                    />
                  ))}
                  {overflow > 0 && (
                    <span
                      aria-hidden="true"
                      className="text-xs font-medium text-coval-text-muted"
                    >
                      +{overflow}
                    </span>
                  )}
                </span>
              )}
            </button>
          </PopoverPrimitive.Trigger>,
        )
      ) : (
        /*
         * Fixed-footprint square wrapper. The trigger is absolutely anchored to
         * its right edge and grows leftward, so the original (right-most) dot
         * and the wrapper's bottom-right corner never move as tags are added or
         * removed — which keeps the menu anchored to that corner locked in place.
         */
        <span className="relative inline-flex aspect-square h-control shrink-0 align-middle">
          {withTriggerTooltip(
            <PopoverPrimitive.Trigger asChild {...closedControls}>
              <button
                type="button"
                aria-label={ariaLabel}
                aria-disabled={disabled || undefined}
                {...(disabled ? SWALLOWED_ACTIVATION : undefined)}
                data-testid={dataTestId}
                className={cn(
                  buttonVariants({ variant }),
                  DISABLED_TRIGGER_CLASSES[variant],
                  // Padding only bites once the dots outgrow the square, so it
                  // stays tight: the cluster is what a tags column has to be wide
                  // enough to hold.
                  "absolute right-0 top-0 h-full min-w-full flex-row-reverse gap-[5px] px-1",
                  // Center a lone mark; right-pack (and overflow left) once tags
                  // exceed the square so the right edge stays fixed.
                  selectedCount <= 1 ? "justify-center" : "justify-start",
                  className,
                )}
              >
                {selectedCount === 0 ? (
                  // Nothing to show but what the control does next, and from
                  // empty that is only ever "add" — unless it cannot, in which
                  // case the cell reads as empty rather than as an invitation.
                  readOnly ? (
                    <span
                      className="text-xs text-coval-text-muted"
                      aria-hidden="true"
                    >
                      –
                    </span>
                  ) : (
                    <Plus
                      className="size-3.5 shrink-0 text-coval-text-muted"
                      aria-hidden="true"
                    />
                  )
                ) : (
                  <>
                    {/* First in DOM ⇒ right-most under flex-row-reverse. */}
                    {overflow > 0 && (
                      <span
                        aria-hidden="true"
                        className="text-xs font-medium text-coval-text-muted"
                      >
                        +{overflow}
                      </span>
                    )}
                    {selectedOptions.slice(0, maxVisible).map((option) => (
                      <ColorSwatch
                        key={option.value}
                        color={resolveColor(option)}
                        className="size-2.5"
                      />
                    ))}
                  </>
                )}
              </button>
            </PopoverPrimitive.Trigger>,
          )}
        </span>
      )}

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={align}
          side={side}
          sideOffset={4}
          avoidCollisions={avoidCollisions}
          className={cn(tagSelectContentVariants())}
          // Land on the search field rather than the content box, so the menu
          // filters from the keystroke that follows the click that opened it.
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            searchRef.current?.focus()
          }}
          onCloseAutoFocus={(event) => {
            if (!restoreFocusOnClose) {
              event.preventDefault()
            }
          }}
        >
          {!readOnly && (
            <div className="p-1">
              <SearchInput
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClear={() => setSearch("")}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canCreate) {
                    e.preventDefault()
                    handleCreate()
                  }
                }}
              />
            </div>
          )}

          {/*
           * `overflow-x-hidden` is load-bearing: a lone `overflow-y` computes
           * the other axis to `auto`, so a row that outgrew the menu by a few
           * pixels would let the whole list scroll sideways. Rows truncate.
           */}
          <div className="max-h-64 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {isLoading ? (
              <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
                Loading tags...
              </p>
            ) : (
              <>
                <SearchResultsStatus
                  emptyState={
                    !hasResults ? (
                      <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
                        {options.length === 0 ? emptyLabel : noResultsLabel}
                      </p>
                    ) : null
                  }
                  announcement={
                    visibleOptions.length === 0 && canCreate
                      ? `${noResultsLabel}. Press Enter to create ${rawQuery}`
                      : resultCountMessage(search, visibleOptions.length)
                  }
                />
                {visibleOptions.map((option) => {
                  const selected = value.includes(option.value)
                  const canToggle = selected ? canRemove : canAdd
                  const actionLabel = selected
                    ? `Remove ${option.label}`
                    : optionLabel || optionDot
                      ? `Add ${option.label}`
                      : option.label
                  const limitTitle =
                    !selected && atMaxSelected
                      ? `Limit of ${maxSelected} tags reached`
                      : undefined
                  // A blocked row stays focusable and hoverable behind
                  // `aria-disabled`, so it keeps its checkbox role and checked
                  // state and can still show why it is blocked.
                  const withLimitTooltip = (control: React.ReactElement) =>
                    limitTitle ? (
                      <Tooltip content={limitTitle}>{control}</Tooltip>
                    ) : (
                      control
                    )

                  return optionLabel || optionDot ? (
                    /* Slot controls own their clicks, so selection gets its own leading target and the row can stay checkbox, color, name without nesting interactive controls. */
                    <div
                      key={option.value}
                      className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-coval-text hover:bg-surface-default-hover"
                    >
                      {withLimitTooltip(
                        <button
                          type="button"
                          onClick={() => {
                            if (canToggle) {
                              toggle(option.value)
                            }
                          }}
                          aria-disabled={!canToggle || undefined}
                          role="checkbox"
                          aria-checked={selected}
                          aria-label={actionLabel}
                          className="flex shrink-0 items-center self-stretch rounded-sm aria-disabled:opacity-50"
                        >
                          <TagOptionCheckbox checked={selected} />
                        </button>,
                      )}
                      {renderDot(option)}
                      {optionLabel ? (
                        <div className="flex min-w-0 flex-1 items-center">
                          {optionLabel(option, { selected })}
                        </div>
                      ) : (
                        <span className="min-w-0 flex-1 truncate">
                          {option.label}
                        </span>
                      )}
                    </div>
                  ) : (
                    <React.Fragment key={option.value}>
                      {withLimitTooltip(
                        <button
                          type="button"
                          onClick={() => {
                            if (canToggle) {
                              toggle(option.value)
                            }
                          }}
                          aria-disabled={!canToggle || undefined}
                          role="checkbox"
                          aria-checked={selected}
                          aria-label={actionLabel}
                          className="flex w-full min-w-0 items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-coval-text hover:bg-surface-default-hover aria-disabled:opacity-50"
                        >
                          <TagOptionCheckbox checked={selected} />
                          {renderDot(option)}
                          <span className="min-w-0 flex-1 truncate">
                            {option.label}
                          </span>
                        </button>,
                      )}
                    </React.Fragment>
                  )
                })}
                {canCreate && (
                  <button
                    type="button"
                    onClick={handleCreate}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-coval-text hover:bg-surface-default-hover"
                  >
                    <Plus className="size-3 shrink-0 text-coval-text-muted" />
                    <span className="shrink-0 text-coval-text-muted">
                      Create
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {rawQuery}
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

TagSelect.displayName = "TagSelect"

export { TagOptionCheckbox, TagSelect, tagSelectContentVariants }
